import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import UploadAdCreativeButton from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/UploadAdCreativeButton'
import FormDrawerWrapper from '../../../features/components/FormDrawerWrapper'
import SocialAuthChecker from '../../../features/components/SocialAuthChecker'
import SuccessModal from '../../../features/components/Modals/SuccessModal'
import RightWrongDatesMessage from '../../../pages/RightsSummary/DigitalRight/UploadAdForms/RightWrongDatesMessage'
import SkeletonForm from '../../../components/Form/Skeleton'
import UploadTikTokAdCreativeForm from './UploadTikTokAdCreativeForm'

import { ReactComponent as TikTokIcon } from '../../../assets/logos/tiktok-circle-icon.svg'

import useIsInternalAdCreation from '../../../features/hooks/internalAdHooks/useIsInternalAdCreation'
import useUploadAdCreativeConstants from '../../Multiplatform/UploadAdCreative/useUploadAdCreativeConstants'

import { redirectTo } from '../../../helpers/url'

import { clearRightUploadAdData } from '../../../modules/actions/rights'
import { clearTikTokIdentities } from '../../../modules/actions/tikTokConstants'
import { selectedTikTokAdAccountSelector } from '../../../modules/selectors/adAccounts'

import { ROUTE_PARAMS, ROUTES } from '../../../constants/routes'
import { PLATFORM_LABELS, TIKTOK_PLATFORM } from '../../../constants/selectLists/platformList'
import { UPLOAD_TIKTOK_AD_CREATIVE } from '../../../constants/forms'
import { MODAL_TYPE } from '../../../constants/other'

export const CAMPAIGN_FORM = 'campaign_form'
export const LINE_ITEM_FORM = 'line_item_form'
export const AD_FORM = 'ad_form'

const initialRedirectionParams = {
  [ROUTE_PARAMS.campaignId]: '',
  [ROUTE_PARAMS.lineItemId]: ''
}

const UploadTikTokAdCreative = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [redirectionParams, setRedirectionParams] = useState(initialRedirectionParams)

  const { id: tikTokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const isInternalAdCreation = useIsInternalAdCreation()
  const { isFormLoading, isRightEndDateInThePast } = useUploadAdCreativeConstants()

  const ButtonTrigger = useCallback(props => <UploadAdCreativeButton Icon={TikTokIcon} text="TikTok" {...props} />, [])

  const modalRedirectionHandler = useCallback(() => {
    redirectTo(ROUTES.lineItemsSummary, {
      ...redirectionParams,
      [ROUTE_PARAMS.platform]: TIKTOK_PLATFORM,
      [ROUTE_PARAMS.adAccount]: tikTokAdAccountId
    })
  }, [redirectionParams, tikTokAdAccountId])

  const onModalCloseHandler = useCallback(() => {
    setRedirectionParams(initialRedirectionParams)
  }, [])

  const onAfterFormCloseHandler = useCallback(() => {
    dispatch(clearRightUploadAdData(TIKTOK_PLATFORM))
  }, [dispatch])

  const renderDrawerContent = useCallback(() => {
    if (isRightEndDateInThePast) {
      return <RightWrongDatesMessage />
    } else {
      return <UploadTikTokAdCreativeForm setRedirectionParams={setRedirectionParams} />
    }
  }, [isRightEndDateInThePast])

  useEffect(
    () => () => {
      // We clear tiktok identities this level but we fetch them on UploadTikTokAdCreativeForm level
      // it helps to avoid redundant requests in some cases:
      // e.g. tiktok platform is not connected or right dates in the past
      dispatch(clearTikTokIdentities())
    },
    [dispatch]
  )

  return (
    <>
      <FormDrawerWrapper
        formName={UPLOAD_TIKTOK_AD_CREATIVE}
        closeOnSubmit={false}
        OpenDrawerTrigger={ButtonTrigger}
        onAfterFormClose={onAfterFormCloseHandler}
        isFormLoading={isFormLoading}
        SkeletonFormComponent={SkeletonForm}
      >
        <SocialAuthChecker
          platform={TIKTOK_PLATFORM}
          authFailureText={`Please authenticate with ${PLATFORM_LABELS[TIKTOK_PLATFORM]} to upload ad creative.`}
        >
          {renderDrawerContent()}
        </SocialAuthChecker>
      </FormDrawerWrapper>
      <SuccessModal
        successButtonText="Go to Ad Group"
        successButtonAction={modalRedirectionHandler}
        modalType={MODAL_TYPE.rightTikTokAdUploaded}
        onAfterClose={onModalCloseHandler}
        showCloseButton
      >
        <p>
          {t(
            isInternalAdCreation
              ? 'Your ad has been uploaded successfully and is pending review.'
              : 'Your ad has been uploaded successfully.'
          )}
        </p>
      </SuccessModal>
    </>
  )
}

export default UploadTikTokAdCreative
